import {
  useIonAlert,
  IonRow,
  IonCol,
  IonButton,
  IonLoading,
} from '@ionic/react'
import { SegmentedMessage } from 'sms-segments-calculator'
import { useFormContext, useWatch, Controller } from 'react-hook-form'

import { useTextAreaRef } from '../../../utils/forms'
import { MMS_CHARACTER_LIMIT } from '../../../constants/communication'
import SimpleSelectPhoto from '../../../components/communication/SimpleSelectPhoto'
import TakePhoto from '../../../components/communication/TakePhoto'
import { Card, CardContent } from '../../../components/ui/card'
import {
  MergeFieldButtons,
  MergeFieldButton,
} from '../../../components/ui/form/merge-field-buttons'
import { TextArea } from '../../../components/ui/form/textarea'
import { InfoGraphic } from '../../../components/ui/info-graphic'
import { useUpdateBroadcast } from '../mutations'
import { useBroadcastClientCounts, useBroadcastSettings } from '../queries'
import {
  composeEmojiSummary,
  getMaxCharacterCount,
  useComposeShopWithLink,
} from '../utils'
import { BroadcastLink } from './broadcast-link'
import { BroadcastPhoto } from './broadcast-photo'
import { BroadcastShopWithSelect } from './broadcast-shopwith-select'

import type { UpdateBroadcastInputs } from '../broadcast.schema'

type Props = {
  broadcastId: string
  disabled: boolean
}

function BroadcastSmsField({ broadcastId, disabled }: Props) {
  const smsMessageRef = useTextAreaRef()

  const [presentAlert] = useIonAlert()

  const clientCounts = useBroadcastClientCounts(broadcastId)

  const { control, getValues, setValue } =
    useFormContext<UpdateBroadcastInputs>()
  const attachments = useWatch({ control, name: 'setAttachmentsSms' })
  const watchSmsMessage = useWatch({ control, name: 'smsMessage' }) ?? ''
  const [collectionIds, lookIds, productIds] = useWatch({
    control,
    name: ['collectionIdsSms', 'lookIdsSms', 'productIdsSms'],
  })

  const shopWithLink = useComposeShopWithLink({
    productIds: productIds ?? [],
    lookIds: lookIds ?? [],
    collectionIds: collectionIds ?? [],
  })

  const broadcastSettings = useBroadcastSettings()

  const segment = new SegmentedMessage(watchSmsMessage)

  const messageType = getValues('messageType')

  const updateBroadcast = useUpdateBroadcast()

  const onDeletePhoto = () =>
    updateBroadcast.mutate(
      {
        objectId: broadcastId,

        broadcast: {
          imglyString: null,
          setAttachmentsSms: null,
        },
      },
      {
        onSuccess: () => {
          setValue('setAttachmentsSms', null)
        },
      }
    )

  if (!broadcastSettings.data) return <IonLoading isOpen message="Loading..." />

  const maxCharacterCount = attachments?.length
    ? MMS_CHARACTER_LIMIT
    : getMaxCharacterCount(broadcastSettings.data.smsSegmentLimit, {
        isNonGsm: Boolean(segment.getNonGsmCharacters().length),
      })

  function updateSmsSegments() {
    return `${segment.numberOfCharacters}/${maxCharacterCount}`
  }

  return (
    <>
      {/* SMS */}
      {messageType === 'sms' || messageType === 'prioSmsEmail' ? (
        <div className="py-2">
          <IonRow className="ion-padding-horizontal ion-justify-content-between ion-align-items-center">
            <IonCol size="auto">
              <h5>Text Message:</h5>
            </IonCol>
            <IonCol size="auto">
              <IonButton
                // routerLink={`${url}/clients?list=sms`}
                size="small"
                color="tertiary"
                fill="clear"
                className="pointer-events-none"
              >
                {clientCounts.data?.smsClientCount} Clients
              </IonButton>
            </IonCol>
          </IonRow>
          <Card>
            <CardContent>
              {!disabled ? (
                <div className="flex justify-end">
                  <IonButton
                    size="small"
                    fill="outline"
                    onClick={() => setValue('smsMessage', '')}
                  >
                    Clear
                  </IonButton>
                </div>
              ) : null}
              <Controller
                control={control}
                name="smsMessage"
                render={({ field: { value, onChange } }) => (
                  <TextArea
                    ref={smsMessageRef}
                    value={value}
                    onIonChange={onChange}
                    placeholder="Write your text here..."
                    className="min-h-16.5"
                    autoGrow
                    autocapitalize="on"
                    disabled={disabled}
                  />
                )}
              />

              {segment.getNonGsmCharacters().length ? (
                <InfoGraphic color="dark">
                  <p>
                    Your credit character amount has been decreased because the
                    following emojis are in your message:{' '}
                    {composeEmojiSummary(
                      getValues('smsMessage') ?? '',
                      segment.getNonGsmCharacters()
                    )}
                  </p>
                </InfoGraphic>
              ) : null}

              <IonRow className="ion-justify-content-between">
                {!disabled ? (
                  <IonCol size="auto" className="space-x-2">
                    <MergeFieldButtons
                      mergeRef={smsMessageRef}
                      onInsert={(value) => setValue('smsMessage', value)}
                    >
                      <MergeFieldButton placeholder="<First Name>">
                        First Name
                      </MergeFieldButton>
                      <MergeFieldButton placeholder="<Last Name>">
                        Last Name
                      </MergeFieldButton>
                      <MergeFieldButton placeholder="<Lifetime Loyalty Points>">
                        Lifetime Loyalty Points
                      </MergeFieldButton>
                      <MergeFieldButton placeholder="<Active Loyalty Points>">
                        Active Loyalty Points
                      </MergeFieldButton>
                      <MergeFieldButton placeholder="<Active Loyalty Visits>">
                        Active Loyalty Visits
                      </MergeFieldButton>
                      <MergeFieldButton placeholder="<Active Loyalty Rewards>">
                        Active Loyalty Rewards
                      </MergeFieldButton>
                    </MergeFieldButtons>
                  </IonCol>
                ) : null}
                <IonCol size="auto" className="ion-align-self-end">
                  <p
                    className={
                      watchSmsMessage.length > maxCharacterCount
                        ? 'text-ion-color-danger'
                        : 'text-ion-color-dark'
                    }
                  >
                    {updateSmsSegments()}
                  </p>
                </IonCol>
              </IonRow>
              {shopWithLink ? (
                <BroadcastLink
                  shopWithLink={shopWithLink}
                  onDelete={() => {
                    setValue('collectionIdsSms', null)
                    setValue('lookIdsSms', null)
                    setValue('productIdsSms', null)
                  }}
                />
              ) : null}
              {attachments ? (
                <BroadcastPhoto
                  photo={attachments[0]}
                  onDelete={() => {
                    if (getValues('imglyString')) {
                      return presentAlert({
                        header: 'Warning',
                        message:
                          'You are about to delete this photo and its associated edit file. Are you sure?',
                        buttons: [
                          'Cancel',
                          {
                            text: 'Delete',
                            role: 'destructive',
                            handler: onDeletePhoto,
                          },
                        ],
                      })
                    }

                    setValue('setAttachmentsSms', null)
                  }}
                />
              ) : null}
            </CardContent>
          </Card>

          {!disabled ? (
            <div className="flex justify-end gap-x-2 px-4 py-2">
              <IonButton
                size="small"
                color="yellow"
                routerLink={`./${broadcastId}/imgly`}
              >
                {getValues('imglyString') ? 'Edit Graphic' : 'Design Graphic'}
              </IonButton>
              <TakePhoto
                onTakePhoto={(e) => {
                  setValue('setAttachmentsSms', [e])
                }}
              />
              <SimpleSelectPhoto
                onSelectPhoto={(e) => {
                  setValue('setAttachmentsSms', [e])
                }}
              />
              <BroadcastShopWithSelect
                onConfirm={(type, value) => {
                  setValue(`${type}Sms`, value)
                }}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  )
}

export { BroadcastSmsField }
